import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PlusIcon, TrashIcon, ExternalLinkIcon, RefreshCwIcon, LogOutIcon, SearchIcon, XIcon, ClockIcon, MoonIcon, SunIcon } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sales-env.combohr.io';

function App() {
  const [sessions, setSessions] = useState([]);
  const [reviewName, setReviewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [filter, setFilter] = useState('me');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentView, setCurrentView] = useState('sessions'); // 'sessions' or 'history'
  const [activities, setActivities] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Filtering and sorting functions
  const filterAndSortSessions = (sessions) => {
    let filtered = [...sessions];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(session =>
        session.reviewName.toLowerCase().includes(query) ||
        session.creatorName?.toLowerCase().includes(query) ||
        session.creatorEmail?.toLowerCase().includes(query)
      );
    }

    // Main filter (all/me)
    if (filter === 'me') {
      filtered = filtered.filter(session => session.creatorEmail === user?.email);
    }

    // Simple sorting by creation date (newest first)
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return filtered;
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
  };

  // Load activities from API
  const loadActivities = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/activities`, {
        headers: getAuthHeaders(),
      });
      setActivities(response.data.activities || []);
    } catch (err) {
      console.error('Error loading activities:', err);
      handleAuthError(err);
    }
  }, [token]);

  // Clear activities via API
  const clearActivitiesHistory = async () => {
    if (!token) return;

    try {
      await axios.delete(`${API_BASE_URL}/activities`, {
        headers: getAuthHeaders(),
      });
      setActivities([]);
      toast.success('History cleared');
    } catch (err) {
      console.error('Error clearing activities:', err);
      handleAuthError(err);
      toast.error('Error clearing history');
    }
  };

  // Authentication functions
  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      logout();
      toast.error('Session expired, please sign in again');
      login();
    }
    return err;
  };

  const login = () => {
    window.location.href = `https://sales-env.combohr.io/auth/google`;
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
          headers: getAuthHeaders()
        });
      }
    } catch (err) {
      console.error('Error during logout:', err);
    } finally {
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('jwt_token');
    }
  };

  const fetchUserInfo = async (authToken) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data.user);
      return true;
    } catch (err) {
      console.error('Error loading user information:', err);
      return false;
    }
  };

  const fetchSessions = useCallback(async (showLoading = true) => {
    if (!isAuthenticated) return;

    if (showLoading) {
      setLoading(true);
    } else {
      setAutoRefreshing(true);
    }
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/deployments?filter=${filter}`, {
        headers: getAuthHeaders()
      });
      setSessions(response.data.deployments || []);
      setLastRefresh(new Date());
    } catch (err) {
      handleAuthError(err);
      const errorMsg = `Error loading: ${err.message}`;
      setError(errorMsg);
      if (showLoading) {
        toast.error(errorMsg);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      } else {
        setAutoRefreshing(false);
      }
    }
  }, [isAuthenticated, filter, token]);

  const createSession = async (e) => {
    e.preventDefault();
    if (!reviewName.trim()) return;

    setCreating(true);
    setError('');
    setSuccess('');

    try {
      const requestData = {
        reviewName: reviewName.trim(),
      };

      await axios.post(`${API_BASE_URL}/deploy`, requestData, {
        headers: getAuthHeaders()
      });

      const successMsg = `Session "${reviewName}" created successfully!`;
      setSuccess(successMsg);
      toast.success(successMsg);
      setReviewName('');
      setShowCreateModal(false);
      await fetchSessions();
      await loadActivities(); // Refresh activities after creation
    } catch (err) {
      handleAuthError(err);
      const errorMsg = `Error creating: ${err.response?.data?.error || err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const deleteSession = async (name) => {
    if (!window.confirm(`Are you sure you want to delete session "${name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/deployments/${name}`, {
        headers: getAuthHeaders()
      });
      const successMsg = `Session "${name}" deleted successfully!`;
      setSuccess(successMsg);
      toast.success(successMsg);
      await fetchSessions();
      await loadActivities(); // Refresh activities after deletion
    } catch (err) {
      handleAuthError(err);
      const errorMsg = `Error deleting: ${err.response?.data?.error || err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const getStatusColor = (syncStatus, healthStatus) => {
    if (healthStatus === 'Healthy') {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (healthStatus === 'Progressing') {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else if (healthStatus === 'Missing' || healthStatus === 'Degraded') {
      return 'bg-red-100 text-red-800 border-red-200';
    } else {
      return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US');
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', JSON.stringify(newDarkMode));
  };

  // Load activities when token is available
  useEffect(() => {
    if (token) {
      loadActivities();
    }
  }, [token, loadActivities]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + K to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        if (searchInput) {
          searchInput.focus();
        }
      }

      // Escape to clear search
      if (e.key === 'Escape') {
        if (searchQuery) {
          setSearchQuery('');
        }
      }

      // Ctrl/Cmd + R to refresh
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault();
        fetchSessions();
      }

      // Ctrl/Cmd + Shift + C to clear search
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        clearSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery, fetchSessions]);

  // Authentication initialization
  useEffect(() => {
    // Check for token from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');

    if (tokenFromUrl) {
      // Clear URL parameters
      window.history.replaceState(null, null, window.location.pathname);

      setToken(tokenFromUrl);
      localStorage.setItem('jwt_token', tokenFromUrl);

      // Fetch user info with new token
      fetchUserInfo(tokenFromUrl).then((success) => {
        if (success) {
          setIsAuthenticated(true);
        }
      });
    } else {
      // Check for stored token
      const storedToken = localStorage.getItem('jwt_token');
      if (storedToken) {
        setToken(storedToken);

        // Validate stored token by fetching user info
        fetchUserInfo(storedToken).then((success) => {
          if (success) {
            setIsAuthenticated(true);
          } else {
            // Token is invalid, remove it
            localStorage.removeItem('jwt_token');
          }
        });
      }
    }
  }, []);

  // Data fetching effect
  useEffect(() => {
    if (isAuthenticated) {
      fetchSessions();

      // Auto-refresh every 10 seconds - silent refresh without loading indicator
      const interval = setInterval(() => {
        fetchSessions(false);
      }, 10000);

      // Cleanup interval on component unmount
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, filter, fetchSessions]);

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Sales Session Manager
            </h1>
            <p className="text-gray-600 mb-8">
              Sign in to manage your sessions
            </p>
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-red-600 text-sm">{error}</div>
              </div>
            )}
            <button
              onClick={login}
              className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={darkMode ? 'min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900' : 'min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50'}>
      {/* Header */}
      <header className={darkMode ? 'bg-gray-800/95 backdrop-blur-md border-b border-gray-700/60 sticky top-0 z-40 shadow-sm' : 'bg-white/95 backdrop-blur-md border-b border-gray-200/60 sticky top-0 z-40 shadow-sm'}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Title */}
            <div className="flex items-center space-x-4 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-md flex-shrink-0" style={{backgroundColor: '#468681'}}>
                <span className="text-white font-bold text-sm">SE</span>
              </div>
              <div className="flex flex-col justify-center min-w-0 flex-1">
                <h1 className={darkMode ? 'text-base font-bold text-white leading-tight whitespace-nowrap' : 'text-base font-bold text-gray-900 leading-tight whitespace-nowrap'}>
                  Session Manager
                </h1>
                <div className={darkMode ? 'flex items-center text-xs text-gray-400 leading-tight' : 'flex items-center text-xs text-gray-500 leading-tight'}>
                  <span className="whitespace-nowrap">ComboHR Demo Sessions</span>
                  {autoRefreshing && (
                    <>
                      <span className="mx-2">•</span>
                      <div className="flex items-center whitespace-nowrap" style={{color: '#468681'}}>
                        <RefreshCwIcon className="h-3 w-3 animate-spin mr-1" />
                        <span>Refreshing...</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center space-x-4">
              </div>

              {/* Discrete Links and Dark Mode Toggle */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={toggleDarkMode}
                  className={darkMode ? 'p-1 text-gray-400 hover:text-[#468681] transition-colors' : 'p-1 text-gray-500 hover:text-[#468681] transition-colors'}
                  title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {darkMode ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
                </button>

                {currentView === 'history' && (
                  <button
                    onClick={() => setCurrentView('sessions')}
                    className={darkMode ? 'text-xs text-gray-400 hover:text-[#468681] transition-colors underline' : 'text-xs text-gray-500 hover:text-[#468681] transition-colors underline'}
                  >
                    ← Back to Sessions
                  </button>
                )}
                {currentView === 'sessions' && (
                  <button
                    onClick={() => setCurrentView('history')}
                    className={darkMode ? 'text-xs text-gray-400 hover:text-[#468681] transition-colors underline flex items-center mr-4' : 'text-xs text-gray-500 hover:text-[#468681] transition-colors underline flex items-center mr-4'}
                  >
                    <ClockIcon className="h-3 w-3 mr-1" />
                    History
                  </button>
                )}
            </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-6">
              {/* User Info */}
              {user && (
                <div className={darkMode ? 'flex items-center space-x-2 px-5 py-1.5 bg-gray-700 rounded-lg' : 'flex items-center space-x-2 px-5 py-1.5 bg-gray-50 rounded-lg'}>
                  {user.picture && (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className={darkMode ? 'w-6 h-6 rounded-full ring-2 ring-gray-600 shadow-sm' : 'w-6 h-6 rounded-full ring-2 ring-white shadow-sm'}
                    />
                  )}
                  <div className="hidden md:block text-xs">
                    <div className={darkMode ? 'font-medium text-white truncate max-w-40' : 'font-medium text-gray-900 truncate max-w-40'}>{user.name}</div>
                    <div className={darkMode ? 'text-gray-400 truncate max-w-52' : 'text-gray-500 truncate max-w-52'}>{user.email}</div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={fetchSessions}
                  disabled={loading}
                  className={darkMode ? 'p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-all disabled:opacity-50' : 'p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-50'}
                  title="Refresh"
                >
                  <RefreshCwIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={logout}
                  className={darkMode ? 'p-2 text-gray-400 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-all' : 'p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all'}
                  title="Déconnexion"
                >
                  <LogOutIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="text-red-600 text-sm">{error}</div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="text-green-600 text-sm">{success}</div>
            </div>
          </div>
        )}

        {/* Content based on current view */}
        {currentView === 'sessions' ? (
          /* Sessions List */
          <div className={darkMode ? 'bg-gray-800 shadow-lg rounded-xl border border-gray-700/60' : 'bg-white shadow-lg rounded-xl border border-gray-200/60'}>
          {/* Header */}
          <div className={darkMode ? 'px-6 py-4 border-b border-gray-700/60' : 'px-6 py-4 border-b border-gray-200/60'}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h2 className={darkMode ? 'text-xl font-bold text-white' : 'text-xl font-bold text-gray-900'}>Sessions</h2>
                  <div className={darkMode ? 'flex items-center px-2 py-1 bg-blue-900/30 text-blue-300 rounded-lg text-sm font-medium' : 'flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium'}>
                    <span>{filterAndSortSessions(sessions).length}</span>
                    <span className={darkMode ? 'mx-1 text-blue-500' : 'mx-1 text-blue-400'}>/</span>
                    <span>{sessions.length}</span>
                  </div>
                </div>
                {lastRefresh && (
                  <div className={darkMode ? 'flex items-center text-xs text-gray-400' : 'flex items-center text-xs text-gray-500'}>
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                    <span>{lastRefresh.toLocaleTimeString('en-US')}</span>
                    <span className="ml-2 opacity-60">(auto 10s)</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 text-white rounded-lg shadow-sm transition-all duration-200 font-medium text-sm group ml-4"
                style={{backgroundColor: '#468681'}}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#3a6b66'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#468681'}
              >
                <PlusIcon className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">New Session</span>
                <span className="sm:hidden">New</span>
              </button>
            </div>

            {/* Search & Filters */}
            <div className="space-y-3">
              {/* Search Bar */}
              <div className="relative">
                <SearchIcon className={darkMode ? 'absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500' : 'absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400'} />
                <input
                  type="text"
                  placeholder="Search by name, creator... (⌘K)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={darkMode ? 'w-full pl-10 pr-10 py-2 border border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-700 hover:bg-gray-600 transition-colors text-white placeholder-gray-400' : 'w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50 hover:bg-white transition-colors'}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={darkMode ? 'absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-600 rounded transition-colors' : 'absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors'}
                  >
                    <XIcon className={darkMode ? 'h-3 w-3 text-gray-400' : 'h-3 w-3 text-gray-400'} />
                  </button>
                )}
              </div>

              {/* Simple Filter */}
              <div className="flex gap-2 items-center">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className={darkMode ? 'px-3 py-1.5 border border-gray-600 rounded-lg text-sm bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white' : 'px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'}
                >
                  <option value="all">All Sessions</option>
                  <option value="me">My Sessions</option>
                </select>

                {/* Clear Search */}
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className={darkMode ? 'flex items-center px-2 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg text-xs font-medium transition-colors' : 'flex items-center px-2 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-xs font-medium transition-colors'}
                  >
                    <XIcon className="h-3 w-3 mr-1" />
                    Clear Search
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="px-6 py-8 text-center">
              <RefreshCwIcon className="h-8 w-8 mx-auto text-gray-400 animate-spin mb-4" />
              <p className="text-gray-500">Loading sessions...</p>
            </div>
          ) : (() => {
            const filteredSessions = filterAndSortSessions(sessions);

            if (sessions.length === 0) {
              return (
                <div className="px-6 py-8 text-center">
                  <p className="text-gray-500">No deployed sessions</p>
                </div>
              );
            }

            if (filteredSessions.length === 0) {
              return (
                <div className="px-6 py-8 text-center">
                  <p className="text-gray-500">No sessions match the search criteria</p>
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              );
            }

            return (
              <div className="p-4 space-y-3">
                {filteredSessions.map((session) => (
                <div key={session.reviewName} className={darkMode ? 'bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 hover:shadow-md transition-all duration-200 p-4' : 'bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 p-4'}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      {/* Avatar + Name */}
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-semibold text-sm">
                            {session.reviewName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <h3 className={darkMode ? 'text-base font-semibold text-white truncate' : 'text-base font-semibold text-gray-900 truncate'}>
                            {session.reviewName}
                          </h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session.syncStatus, session.healthStatus)}`}>
                              {session.healthStatus || 'Unknown'}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${session.syncStatus === 'Unknown' ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'}`}>
                              {session.syncStatus === 'OutOfSync' ? 'Synced*' : (session.syncStatus || 'Unknown')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Creator & Date */}
                      <div className={darkMode ? 'hidden md:flex items-center space-x-3 text-xs text-gray-400' : 'hidden md:flex items-center space-x-3 text-xs text-gray-500'}>
                        {session.creatorName && (
                          <div className="flex items-center space-x-1">
                            {session.creatorPicture && (
                              <img src={session.creatorPicture} alt={session.creatorName} className="h-4 w-4 rounded-full" />
                            )}
                            <span className="truncate max-w-24">{session.creatorName}</span>
                          </div>
                        )}
                        <span>•</span>
                        <span>{new Date(session.createdAt).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>

                    {/* Actions & Links */}
                    <div className="flex items-center space-x-2 ml-4">
                      {/* Quick Links */}
                      <div className="flex items-center space-x-1">
                        <a
                          href={session.frontendUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded text-xs font-medium transition-colors"
                          title="Frontend"
                        >
                          <ExternalLinkIcon className="h-3 w-3 mr-1" />
                          App
                        </a>
                        {session.payrollUrl && (
                          <a
                            href={session.payrollUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center px-2 py-1 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded text-xs font-medium transition-colors"
                            title="Payroll"
                          >
                            <ExternalLinkIcon className="h-3 w-3 mr-1" />
                            Payroll
                          </a>
                        )}
                      </div>

                      {/* Delete */}
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteSession(session.reviewName); }}
                          className={darkMode ? 'p-2 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-900/20 transition-all' : 'p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all'}
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            );
          })()}
        </div>
        ) : (
          /* History View */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Activity History</h3>
            </div>

            <div className="divide-y divide-gray-200">
              {activities.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <div className="text-gray-500">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-sm font-medium text-gray-900 mb-2">No Activity</h3>
                    <p className="text-sm text-gray-500">Activity history will appear here.</p>
                  </div>
                </div>
              ) : (
                activities.map((activity) => (
                  <div key={activity.id} className="px-6 py-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {activity.action === 'create' ? (
                          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-100">
                            <PlusIcon className="h-4 w-4 text-green-600" />
                          </div>
                        ) : activity.action === 'delete' ? (
                          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-red-100">
                            <TrashIcon className="h-4 w-4 text-red-600" />
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100">
                            <ClockIcon className="h-4 w-4 text-blue-600" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {activity.details}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          By {activity.user_name} • {new Date(activity.created_at).toLocaleDateString('en-US', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm transition-opacity"
              onClick={() => setShowCreateModal(false)}
            ></div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-2xl px-6 pt-5 pb-4 text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-8">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-xl text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 p-2 hover:bg-gray-50 transition-colors"
                  onClick={() => setShowCreateModal(false)}
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 sm:mx-0 sm:h-12 sm:w-12">
                  <PlusIcon className="h-8 w-8 text-white sm:h-6 sm:w-6" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-2xl leading-6 font-bold text-gray-900 mb-2">
                    Create a New Session
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Créez une nouvelle session de démonstration ComboHR
                  </p>
                </div>
              </div>

              <form onSubmit={createSession} className="mt-6">
                <div className="space-y-6">
                  {/* Review Name */}
                  <div>
                    <label htmlFor="reviewName" className="block text-sm font-semibold text-gray-900 mb-3">
                      Nom de la session *
                    </label>
                    <input
                      type="text"
                      id="reviewName"
                      value={reviewName}
                      onChange={(e) => setReviewName(e.target.value)}
                      placeholder="ex: demo-client-xyz"
                      pattern="^[a-z0-9]([a-z0-9\-]*[a-z0-9])?$"
                      title="Must be lowercase, alphanumeric with dashes only"
                      className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-400"
                      required
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      Uniquement des lettres minuscules, chiffres et tirets (3-63 caractères)
                    </p>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3">
                  <button
                    type="button"
                    className="mt-3 sm:mt-0 inline-flex justify-center px-6 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={creating || !reviewName.trim()}
                    className="inline-flex justify-center px-8 py-3 border border-transparent rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-lg"
                  >
                    {creating ? (
                      <>
                        <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" />
                        Création en cours...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Create Session
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

export default App;